import { Car, Pencil, GitMerge, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Vehicle {
  id: string;
  name: string;
  year: number;
  make: string;
  model: string;
  trim?: string | null;
  trim_variation?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  color?: string | null;
  mileage?: number | null;
  image_url?: string | null;
}

interface VehicleListItemProps {
  vehicle: Vehicle;
  onEdit: (vehicle: Vehicle) => void;
  onMerge: (vehicle: Vehicle) => void;
  onDelete: (vehicle: Vehicle) => void;
  showMerge?: boolean;
}

export const VehicleListItem = ({ vehicle, onEdit, onMerge, onDelete, showMerge = true }: VehicleListItemProps) => {
  const fullName = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ""}`;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 md:gap-4">
          <div className="flex items-start gap-3 md:gap-4 min-w-0 flex-1">
            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {vehicle.image_url ? (
                <img
                  src={vehicle.image_url}
                  alt={vehicle.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Car className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div className="space-y-1 min-w-0">
              <h3 className="font-semibold text-foreground truncate">{vehicle.name}</h3>
              <p className="text-sm text-muted-foreground truncate">{fullName}</p>
              {vehicle.trim_variation && (
                <p className="text-xs text-muted-foreground truncate">{vehicle.trim_variation}</p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                {vehicle.license_plate && (
                  <span>Plate: {vehicle.license_plate}</span>
                )}
                {vehicle.color && <span>Color: {vehicle.color}</span>}
                {vehicle.mileage !== null && vehicle.mileage !== undefined && (
                  <span>Mileage: {vehicle.mileage.toLocaleString()} mi</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0 flex-shrink-0">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(vehicle)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {showMerge && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMerge(vehicle)}>
                      <GitMerge className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Merge</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onDelete(vehicle)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
